import type ReactDOM from 'react-dom/client'
import { setRestaurantId, seedShowcaseStores, type RestaurantService } from '@restaurant/core'
import { initializeWebBrand } from '../config/brand'
import { showcaseDevice, type ShowcasePayload } from './showcase'
import { installCustomizeListener } from './customize'
import { buildShowcaseRestaurant, SHOWCASE_RESTAURANT_ID } from './fixtures'
import { injectShowcaseStatusBar } from './statusBar'

// 5 web suites import seedShowcaseStores FROM './boot' — re-export it here.
export { seedShowcaseStores }

/**
 * Worker handshake: waits for [data-showcase-state], branches on value.
 *
 * Frame-readiness bridge (surfaces spec §2): the preview page cannot read
 * our marker cross-origin; a message clears its shimmer / triggers its
 * fallback card. Post ONLY to the embedding page's origin (from referrer);
 * '*' would broadcast lead data signals to any embedder. getParent is
 * injectable for tests — jsdom's window.parent getter can be finicky to
 * spy on; the contract (post only when framed, only to the referrer
 * origin) is what matters, not the mechanism.
 */
export function markShowcaseState(
  state: 'ready' | 'error',
  getParent: () => Window | null = () => (window.parent !== window ? window.parent : null),
): void {
  document.documentElement.dataset.showcaseState = state
  try {
    const parent = getParent()
    if (!parent || !document.referrer) return
    const device = showcaseDevice()
    parent.postMessage(device ? { showcase: state, device } : { showcase: state }, new URL(document.referrer).origin)
  } catch {
    /* unframed or opaque referrer — worker path lands here, fine */
  }
}

/** fonts.ready with a bound — a stalled font fetch must not hang the marker. */
async function fontsSettled(timeoutMs = 5000): Promise<void> {
  const ready = document.fonts?.ready
  if (!ready) return
  await Promise.race([ready.then(() => undefined), new Promise<void>((r) => setTimeout(r, timeoutMs))])
}

/** ErrorBoundary (apps/web/src/components/ErrorBoundary.tsx) stamps this on
 * its fallback root when it catches a render crash instead of committing
 * children. */
const ERROR_BOUNDARY_SELECTOR = '[data-error-boundary]'
/** MenuPage's PageContentMenu (packages/core) carries this testID → renders
 * as data-testid on web. Positive signal that real menu content committed. */
const SHOWCASE_CONTENT_SELECTOR = '[data-testid="menu-page"]'

/**
 * True when the just-committed DOM is the ErrorBoundary fallback (a render
 * crash inside <App/> is swallowed by the root boundary — it never reaches
 * this function's caller's try/catch) or the expected menu content root
 * never mounted. Spec §5: readiness means "menu list mounted" — a screenshot
 * of the error screen must never reach a lead.
 */
function showcaseRenderFailed(): boolean {
  if (document.querySelector(ERROR_BOUNDARY_SELECTOR)) return true
  if (!document.querySelector(SHOWCASE_CONTENT_SELECTOR)) return true
  return false
}

/** Bounded wait for every currently-mounted <img> to settle (load OR error). */
async function settleImages(timeoutMsPerImage = 4000): Promise<void> {
  const imgs = Array.from(document.images)
  await Promise.all(imgs.map((img) => {
    if (img.complete) return Promise.resolve()
    return new Promise<void>((resolve) => {
      const done = () => { clearTimeout(t); resolve() }
      const t = setTimeout(done, timeoutMsPerImage)
      img.addEventListener('load', done, { once: true })
      img.addEventListener('error', done, { once: true })
    })
  }))
}

/**
 * Showcase boot: no resolveTenant, no LoadingScreen animation loop, no
 * prefetch. Called by initializeApp() when the showcase hash is detected.
 * renderAppFn is main.tsx's renderApp — injected to avoid a circular import.
 * payload is resolved by the caller (hash mode: getShowcasePayload(); token
 * mode: the awaited fetchTokenPayload() result) — null covers both the
 * invalid-hash and the failed/unknown/revoked-token cases indistinguishably
 * (no oracle).
 */
export async function bootShowcase(
  root: ReactDOM.Root,
  renderAppFn: (root: ReactDOM.Root, remountKey?: number) => void,
  payload: ShowcasePayload | null,
): Promise<void> {
  if (!payload) {
    markShowcaseState('error')
    return
  }
  try {
    setRestaurantId(SHOWCASE_RESTAURANT_ID)
    const restaurant = buildShowcaseRestaurant(payload)
    // Fake service reuses the WHOLE brand chain (dathanna ramp, CSS vars,
    // document title, favicon) with zero replication. Hex is the payload's
    // effective brand (validated in buildShowcaseRestaurant, forced-red
    // fallback for missing/malformed values).
    const fakeService = { getRestaurant: async () => restaurant } as unknown as RestaurantService
    await initializeWebBrand(fakeService, SHOWCASE_RESTAURANT_ID)
    seedShowcaseStores(payload, restaurant)
    // Live customization from the embedding preview page (no-op when unframed).
    // Live-verified (2026-07-29): a color apply repaints CSS custom
    // properties but not components that captured a color into a JS style
    // literal at first mount. remount re-keys <App/> so a color apply forces
    // a full app-tree remount — name-only messages never touch this.
    let remountKey = 0
    installCustomizeListener(undefined, undefined, {
      remount: () => { remountKey += 1; renderAppFn(root, remountKey) },
    })
    renderAppFn(root)
    // Glyphs read against the brand-derived chrome painted behind them, so
    // pass the SAME hex the theme was seeded with — statusBarForeground()
    // computes per-hex contrast (light brand → dark glyphs).
    injectShowcaseStatusBar(restaurant.primaryColor, showcaseDevice())

    // Readiness: first commit (double-rAF) → fonts → images. Spec §5.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
    // A render crash inside <App/> is caught by the root ErrorBoundary and
    // never throws here — check the committed DOM directly so a screenshot
    // of the error screen never gets marked 'ready' and shipped to a lead.
    if (showcaseRenderFailed()) {
      markShowcaseState('error')
      return
    }
    await fontsSettled()
    await settleImages()
    markShowcaseState('ready')
  } catch (err) {
    console.error('[showcase] boot failed', err)
    markShowcaseState('error')
  }
}
